import { useEffect, useMemo, useState } from 'react'
import { supabase } from '@/lib/supabase'

type Money = number | null | undefined

interface LocalDailyRow {
  metric_date: string
  leads_count: number | null | undefined
  appointments_count: number | null | undefined
  new_visitors_count: number | null | undefined
  new_sales_count: number | null | undefined
  renewals_count: number | null | undefined
  upgrades_count: number | null | undefined
  returning_clients_count: number | null | undefined
  new_subscriptions_count: number | null | undefined
  subscription_renewals_count: number | null | undefined
  subscription_upgrades_count: number | null | undefined
  subscription_returning_clients_count: number | null | undefined
  total_clients_count: number | null | undefined
  active_subscribers_count: number | null | undefined
  marketing_expenses_amount: Money
  new_sales_amount: Money
  renewals_amount: Money
  upgrades_amount: Money
  returning_clients_amount: Money
  new_subscriptions_amount: Money
  membership_fees_new_amount: Money
  membership_fees_subscription_upgrades_amount: Money
  membership_fees_subscription_returning_amount: Money
  subscription_renewals_amount: Money
  subscription_upgrades_amount: Money
  subscription_returning_clients_amount: Money
  trial_payments_amount: Money
  private_sessions_amount: Money
  other_income: Money
  refunds_amount: Money
  gross_revenue: Money
}

interface RubDailyRow {
  metric_date: string
  currency_code: string | null
  marketing_expenses_amount_rub: Money
  new_sales_amount_rub: Money
  renewals_amount_rub: Money
  upgrades_amount_rub: Money
  returning_clients_amount_rub: Money
  new_subscriptions_amount_rub: Money
  membership_fees_new_amount_rub: Money
  membership_fees_subscription_upgrades_amount_rub: Money
  membership_fees_subscription_returning_amount_rub: Money
  subscription_renewals_amount_rub: Money
  subscription_upgrades_amount_rub: Money
  subscription_returning_clients_amount_rub: Money
  trial_payments_amount_rub: Money
  private_sessions_amount_rub: Money
  other_income_rub: Money
  refunds_amount_rub: Money
  gross_revenue_rub: Money
}

interface CrmPaymentRow {
  date: string | null
  amount: Money
  amount_rub: Money
  subscription_sale: string | null
}

interface FinanceCalendarPayload {
  local_rows: LocalDailyRow[]
  rub_rows: RubDailyRow[]
  crm_rows: CrmPaymentRow[]
  clients_baseline: number | null
  subscribers_baseline: number | null
}

export interface CalendarMonthRow {
  month_start: string
  year: number
  month: number
  currency_code: string
  regular_new_amount: number
  regular_recurring_amount: number
  subscription_new_amount: number
  subscription_recurring_amount: number
  trial_payments_amount: number
  private_sessions_amount: number
  other_income_amount: number
  refunds_amount: number
  gross_revenue: number
  crm_subscription_revenue: number
  crm_regular_revenue: number
  regular_new_amount_rub: number
  regular_recurring_amount_rub: number
  subscription_new_amount_rub: number
  subscription_recurring_amount_rub: number
  trial_payments_amount_rub: number
  private_sessions_amount_rub: number
  other_income_amount_rub: number
  refunds_amount_rub: number
  gross_revenue_rub: number
  crm_subscription_revenue_rub: number
  crm_regular_revenue_rub: number
  mixed_new_sales_count: number
  mixed_renewals_count: number
  mixed_upgrades_count: number
  mixed_returning_count: number
  start_clients_count: number
  start_subscribers_count: number
  active_clients_count: number
  marketing_spend_amount: number
  marketing_spend_amount_rub: number
  leads_count: number
  appointments_count: number
  trial_visits_count: number
  purchases_after_trial_count: number
  days_with_data: number
}

interface UseCalendarMonthMetricsResult {
  rows: CalendarMonthRow[]
  currencyCode: string
  loading: boolean
  error: string | null
}

function toNumber(value: Money): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function buildMonthStart(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}-01`
}

function normalizeDateOnly(value: unknown): string | null {
  if (!value || typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed
  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return trimmed.slice(0, 10)
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`
}

function getErrorMessage(error: unknown): string {
  if (!error) return ''
  if (typeof error === 'string') return error
  if (typeof error === 'object' && 'message' in error && typeof (error as { message?: unknown }).message === 'string') {
    return (error as { message: string }).message
  }
  return String(error)
}

export function useCalendarMonthMetrics(
  studioInternalId: number | null,
  year: number
): UseCalendarMonthMetricsResult {
  const [rows, setRows] = useState<CalendarMonthRow[]>([])
  const [currencyCode, setCurrencyCode] = useState<string>('RUB')
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const run = async () => {
      if (!studioInternalId) {
        setRows([])
        setCurrencyCode('RUB')
        return
      }

      try {
        setLoading(true)
        setError(null)

        const response = await (supabase as any).rpc('get_finance_calendar_month_metrics', {
          p_studio_internal_id: studioInternalId,
          p_year: year
        })
        if (response.error) throw response.error

        const payload = (response.data || {}) as Partial<FinanceCalendarPayload>
        const localRows = (payload.local_rows || []) as LocalDailyRow[]
        const rubRows = (payload.rub_rows || []) as RubDailyRow[]
        const crmRows = (payload.crm_rows || []) as CrmPaymentRow[]
        const rowsByMonth = new Map<string, LocalDailyRow[]>()

        const rubByDate = new Map<string, RubDailyRow>()
        for (const row of rubRows) {
          rubByDate.set(row.metric_date, row)
        }

        for (const row of localRows) {
          const [y, m] = row.metric_date.split('-').map(Number)
          if (!y || !m) continue
          const key = `${y}-${String(m).padStart(2, '0')}`
          const monthRows = rowsByMonth.get(key) || []
          monthRows.push(row)
          rowsByMonth.set(key, monthRows)
        }

        const monthStartSnapshots = new Map<string, { start_clients_count: number; start_subscribers_count: number }>()
        let latestClientsCount = toNumber(payload.clients_baseline)
        let latestSubscribersCount = toNumber(payload.subscribers_baseline)

        for (let month = 1; month <= 12; month += 1) {
          const key = `${year}-${String(month).padStart(2, '0')}`
          monthStartSnapshots.set(key, {
            start_clients_count: latestClientsCount,
            start_subscribers_count: latestSubscribersCount
          })

          const monthRows = rowsByMonth.get(key) || []
          for (const row of monthRows) {
            if (row.total_clients_count !== null && row.total_clients_count !== undefined) {
              latestClientsCount = toNumber(row.total_clients_count)
            }
            if (row.active_subscribers_count !== null && row.active_subscribers_count !== undefined) {
              latestSubscribersCount = toNumber(row.active_subscribers_count)
            }
          }
        }

        const monthMap = new Map<string, CalendarMonthRow>()
        const daysByMonth = new Map<string, Set<string>>()

        for (const row of localRows) {
          const [y, m] = row.metric_date.split('-').map(Number)
          if (!y || !m) continue

          const key = `${y}-${String(m).padStart(2, '0')}`
          const current = monthMap.get(key) || {
            month_start: buildMonthStart(y, m),
            year: y,
            month: m,
            currency_code: 'RUB',
            regular_new_amount: 0,
            regular_recurring_amount: 0,
            subscription_new_amount: 0,
            subscription_recurring_amount: 0,
            trial_payments_amount: 0,
            private_sessions_amount: 0,
            other_income_amount: 0,
            refunds_amount: 0,
            gross_revenue: 0,
            crm_subscription_revenue: 0,
            crm_regular_revenue: 0,
            regular_new_amount_rub: 0,
            regular_recurring_amount_rub: 0,
            subscription_new_amount_rub: 0,
            subscription_recurring_amount_rub: 0,
            trial_payments_amount_rub: 0,
            private_sessions_amount_rub: 0,
            other_income_amount_rub: 0,
            refunds_amount_rub: 0,
            gross_revenue_rub: 0,
            crm_subscription_revenue_rub: 0,
            crm_regular_revenue_rub: 0,
            mixed_new_sales_count: 0,
            mixed_renewals_count: 0,
            mixed_upgrades_count: 0,
            mixed_returning_count: 0,
            start_clients_count: monthStartSnapshots.get(key)?.start_clients_count || 0,
            start_subscribers_count: monthStartSnapshots.get(key)?.start_subscribers_count || 0,
            active_clients_count: 0,
            marketing_spend_amount: 0,
            marketing_spend_amount_rub: 0,
            leads_count: 0,
            appointments_count: 0,
            trial_visits_count: 0,
            purchases_after_trial_count: 0,
            days_with_data: 0
          }

          const rub = rubByDate.get(row.metric_date)
          if (rub?.currency_code) current.currency_code = rub.currency_code

          current.mixed_new_sales_count +=
            toNumber(row.new_sales_count) +
            toNumber(row.new_subscriptions_count)
          current.mixed_renewals_count +=
            toNumber(row.renewals_count) +
            toNumber(row.subscription_renewals_count)
          current.mixed_upgrades_count +=
            toNumber(row.upgrades_count) +
            toNumber(row.subscription_upgrades_count)
          current.mixed_returning_count +=
            toNumber(row.returning_clients_count) +
            toNumber(row.subscription_returning_clients_count)
          current.marketing_spend_amount += toNumber(row.marketing_expenses_amount)
          current.marketing_spend_amount_rub += toNumber(rub?.marketing_expenses_amount_rub)
          current.leads_count += toNumber(row.leads_count)
          current.appointments_count += toNumber(row.appointments_count)
          current.trial_visits_count += toNumber(row.new_visitors_count)
          current.purchases_after_trial_count +=
            toNumber(row.new_sales_count) +
            toNumber(row.new_subscriptions_count)
          if (row.total_clients_count !== null && row.total_clients_count !== undefined) {
            current.active_clients_count = toNumber(row.total_clients_count)
          }

          current.regular_new_amount += toNumber(row.new_sales_amount)
          current.regular_recurring_amount +=
            toNumber(row.renewals_amount) +
            toNumber(row.upgrades_amount) +
            toNumber(row.returning_clients_amount)
          current.subscription_new_amount +=
            toNumber(row.new_subscriptions_amount) +
            toNumber(row.membership_fees_new_amount)
          current.subscription_recurring_amount +=
            toNumber(row.membership_fees_subscription_upgrades_amount) +
            toNumber(row.membership_fees_subscription_returning_amount) +
            toNumber(row.subscription_renewals_amount) +
            toNumber(row.subscription_upgrades_amount) +
            toNumber(row.subscription_returning_clients_amount)
          current.trial_payments_amount += toNumber(row.trial_payments_amount)
          current.private_sessions_amount += toNumber(row.private_sessions_amount)
          current.other_income_amount += toNumber(row.other_income)
          current.refunds_amount += toNumber(row.refunds_amount)
          current.gross_revenue += toNumber(row.gross_revenue)

          current.regular_new_amount_rub += toNumber(rub?.new_sales_amount_rub)
          current.regular_recurring_amount_rub +=
            toNumber(rub?.renewals_amount_rub) +
            toNumber(rub?.upgrades_amount_rub) +
            toNumber(rub?.returning_clients_amount_rub)
          current.subscription_new_amount_rub +=
            toNumber(rub?.new_subscriptions_amount_rub) +
            toNumber(rub?.membership_fees_new_amount_rub)
          current.subscription_recurring_amount_rub +=
            toNumber(rub?.membership_fees_subscription_upgrades_amount_rub) +
            toNumber(rub?.membership_fees_subscription_returning_amount_rub) +
            toNumber(rub?.subscription_renewals_amount_rub) +
            toNumber(rub?.subscription_upgrades_amount_rub) +
            toNumber(rub?.subscription_returning_clients_amount_rub)
          current.trial_payments_amount_rub += toNumber(rub?.trial_payments_amount_rub)
          current.private_sessions_amount_rub += toNumber(rub?.private_sessions_amount_rub)
          current.other_income_amount_rub += toNumber(rub?.other_income_rub)
          current.refunds_amount_rub += toNumber(rub?.refunds_amount_rub)
          current.gross_revenue_rub += toNumber(rub?.gross_revenue_rub)

          const days = daysByMonth.get(key) || new Set<string>()
          days.add(row.metric_date)
          daysByMonth.set(key, days)

          monthMap.set(key, current)
        }

        for (const row of crmRows) {
          const metricDate = normalizeDateOnly(row.date)
          if (!metricDate) continue

          const [y, m] = metricDate.split('-').map(Number)
          if (!y || !m) continue

          const key = `${y}-${String(m).padStart(2, '0')}`
          const current = monthMap.get(key) || {
            month_start: buildMonthStart(y, m),
            year: y,
            month: m,
            currency_code: 'RUB',
            regular_new_amount: 0,
            regular_recurring_amount: 0,
            subscription_new_amount: 0,
            subscription_recurring_amount: 0,
            trial_payments_amount: 0,
            private_sessions_amount: 0,
            other_income_amount: 0,
            refunds_amount: 0,
            gross_revenue: 0,
            crm_subscription_revenue: 0,
            crm_regular_revenue: 0,
            regular_new_amount_rub: 0,
            regular_recurring_amount_rub: 0,
            subscription_new_amount_rub: 0,
            subscription_recurring_amount_rub: 0,
            trial_payments_amount_rub: 0,
            private_sessions_amount_rub: 0,
            other_income_amount_rub: 0,
            refunds_amount_rub: 0,
            gross_revenue_rub: 0,
            crm_subscription_revenue_rub: 0,
            crm_regular_revenue_rub: 0,
            mixed_new_sales_count: 0,
            mixed_renewals_count: 0,
            mixed_upgrades_count: 0,
            mixed_returning_count: 0,
            start_clients_count: monthStartSnapshots.get(key)?.start_clients_count || 0,
            start_subscribers_count: monthStartSnapshots.get(key)?.start_subscribers_count || 0,
            active_clients_count: 0,
            marketing_spend_amount: 0,
            marketing_spend_amount_rub: 0,
            leads_count: 0,
            appointments_count: 0,
            trial_visits_count: 0,
            purchases_after_trial_count: 0,
            days_with_data: 0
          }

          const amount = toNumber(row.amount)
          const amountRub = toNumber(row.amount_rub)
          if (typeof row.subscription_sale === 'string' && row.subscription_sale.trim() === 'Да') {
            current.crm_subscription_revenue += amount
            current.crm_subscription_revenue_rub += amountRub
          } else {
            current.crm_regular_revenue += amount
            current.crm_regular_revenue_rub += amountRub
          }
          monthMap.set(key, current)
        }

        const monthRows = Array.from(monthMap.values())
          .map((row) => ({
            ...row,
            days_with_data: daysByMonth.get(`${row.year}-${String(row.month).padStart(2, '0')}`)?.size || 0
          }))
          .sort((a, b) => a.month - b.month)

        const firstCurrency = monthRows.find((row) => row.currency_code)?.currency_code || 'RUB'
        setCurrencyCode(firstCurrency)
        setRows(monthRows)
      } catch (err) {
        console.error('Error loading calendar month metrics:', err)
        setError(getErrorMessage(err) || 'Не удалось загрузить помесячные данные')
        setRows([])
      } finally {
        setLoading(false)
      }
    }

    run()
  }, [studioInternalId, year])

  return useMemo(
    () => ({
      rows,
      currencyCode,
      loading,
      error
    }),
    [rows, currencyCode, loading, error]
  )
}
